// Saving and printing a PDF made in the browser.
//
// Computers with Chrome, Edge or Firefox print it straight from the page (a
// hidden frame opens the print window). Safari and iPhones/iPads open it in a
// new tab instead, where Print (or Share, then Print) prints it. Android
// phones can't show a PDF in a tab, so Print saves it like Download, and the
// phone's PDF viewer prints it.

// How long Print waits for the hidden frame to show the PDF.
export const PRINT_FRAME_WAIT = 8000;

const agent = () =>
  typeof navigator === "undefined" ? "" : navigator.userAgent || "";

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(agent()) ||
  (typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1);
export const isAndroid = () => /Android/i.test(agent());
const isSafari = () =>
  /^((?!chrome|chromium|android|crios|fxios|edg).)*safari/i.test(agent());

// "frame" (print from the page), "tab" (open the PDF in a new tab) or "save".
export const printWay = () => {
  if (isAndroid()) return "save";
  if (isIOS() || isSafari()) return "tab";
  // Set to download PDFs instead of showing them (a Chrome and Edge setting),
  // a browser can't print one from the page: it's saved instead.
  if (typeof navigator !== "undefined" && navigator.pdfViewerEnabled === false)
    return "save";
  return "frame";
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/**
 * A tab to show the PDF in, opened right on the click when this browser
 * prints from a tab: opened later, once the PDF is made, it would be
 * blocked as a pop-up. Null elsewhere.
 */
export const openPrintTab = () => {
  if (printWay() !== "tab") return null;
  try {
    return window.open("", "_blank");
  } catch {
    return null;
  }
};

/**
 * Prints `blob`: from the page, in `tab` (see openPrintTab), or by saving it
 * as `filename`. Resolves to what happened: "printed", "opened" or "saved".
 */
export const printBlob = (
  blob,
  { tab = null, filename = "document.pdf" } = {},
) =>
  new Promise((resolve) => {
    const way = printWay();
    if (way === "save") {
      downloadBlob(blob, filename);
      resolve("saved");
      return;
    }
    const url = URL.createObjectURL(blob);
    if (way === "tab" || tab) {
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10 * 60000);
      resolve("opened");
      return;
    }
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText =
      "position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0";
    // A browser set to download PDFs instead of showing them never loads the
    // frame: after a while the PDF is saved instead, so Print never hangs.
    const giveUp = setTimeout(() => {
      frame.onload = null;
      frame.remove();
      URL.revokeObjectURL(url);
      downloadBlob(blob, filename);
      resolve("saved");
    }, PRINT_FRAME_WAIT);
    frame.onload = () => {
      clearTimeout(giveUp);
      // The PDF viewer needs a moment after the frame loads, or it prints blank.
      setTimeout(() => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
          // Back to the page, so its keys (Esc to close a window) work again.
          window.focus();
          resolve("printed");
        } catch {
          window.open(url, "_blank");
          resolve("opened");
        }
        setTimeout(() => {
          frame.remove();
          URL.revokeObjectURL(url);
        }, 60000);
      }, 300);
    };
    frame.src = url;
    document.body.appendChild(frame);
  });

// A file name without characters some systems refuse.
export const pdfName = (...parts) =>
  `${parts
    .filter(Boolean)
    .map((part) =>
      String(part)
        .replace(/[\\/:*?"<>|]+/g, "-")
        .trim(),
    )
    .join(" - ")}.pdf`;
